import { LinearProgress } from '@mui/material';
import React, { MutableRefObject, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllFilterValues } from '../../../../api/data/hooks';
import {
  employeeIdDataField,
  MINIMUM_SEARCH_INPUT,
  UNDEFINED_DISPLAY_KEY,
} from '../../../../constants';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import {
  findWithRest,
  getKeyFromDataFieldWithDataType,
} from '../../../../utils';
import { BetterLoading } from '../../BetterLoading';
import { Filter } from '../filterbar/types';
import { MyFilterSection } from '../myfilters/MyFilterSection';
import { ContextLocation, FilterSection, HierarchyItem } from '../types';
import { FilterSearch } from './FilterSearch';
import { FilterTraySectionWrapper } from './FilterTraySectionWrapper';

interface FilterTrayProps<FS extends FilterSection> {
  filterSections: FS[];
  filterHandle: GlobalFilterTypeHandle<Filter>;
  options: { backgroundColors: string[]; myFilters?: { enabled?: boolean } };
  globalSearchRef: MutableRefObject<HTMLInputElement | undefined>;
}

export const FilterTray = <FS extends FilterSection>(
  props: FilterTrayProps<FS>
) => {
  const { t } = useTranslation();
  const { filterSections, filterHandle, options, globalSearchRef } = props;
  const [searchText, setSearchText] = useState('');
  const [isPending, startTransition] = useTransition();
  const { data: result, isFetching } = useAllFilterValues(
    searchText,
    filterSections
  );

  const [individualFilterSection, ...otherFilterSections] = findWithRest(
    filterSections
  )(
    (fs) =>
      fs.dataField.dataType === employeeIdDataField.dataType &&
      fs.dataField.dataField === employeeIdDataField.dataField
  );

  const myFiltersEnabled = options.myFilters?.enabled ?? true;
  return (
    <div style={{ width: '100%' }} data-cy="panalyt-filter-tray">
      <FilterSearch
        setGlobalSearchText={setSearchText}
        startTransition={startTransition}
        globalSearchRef={globalSearchRef}
      />
      {myFiltersEnabled ? (
        // TODO: add error boundary for myfilters
        <MyFilterSection
          filtersHandle={filterHandle}
          filterSections={filterSections}
        />
      ) : null}
      <BetterLoading
        debugString={'FilterTray'}
        isPending={isPending || isFetching}
        fallback={<LinearProgress />}
        wait={0}
        freez={800}
      >
        <>
          {otherFilterSections.map((section, index) => {
            const key = getKeyFromDataFieldWithDataType(section.dataField);
            const searchResult: HierarchyItem[] | null = result?.[key] ?? null;

            return (searchText.length >= MINIMUM_SEARCH_INPUT &&
              searchResult) ||
              searchText.length < MINIMUM_SEARCH_INPUT ||
              section
                .labelFn(t, t(UNDEFINED_DISPLAY_KEY), {
                  location: ContextLocation.FILTERTRAY,
                })
                .toLowerCase()
                .includes(searchText.toLowerCase()) ? (
              <FilterTraySectionWrapper<FS>
                key={`filter-tray-section-wrapper-${section.key.hash}`}
                section={section}
                backgroundColor={
                  options.backgroundColors[
                    index % options.backgroundColors.length
                  ]
                }
                filterHandle={filterHandle}
                searchResult={searchResult}
              />
            ) : null;
          })}
        </>
      </BetterLoading>
      {individualFilterSection ? (
        <FilterTraySectionWrapper<FS>
          key={`filter-tray-section-wrapper-${individualFilterSection.key.hash}`}
          section={individualFilterSection}
          backgroundColor={'rgb(67,67,67)'}
          filterHandle={filterHandle}
        />
      ) : null}
    </div>
  );
};
